import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api, { setVaultTokenGetter } from '../services/api';

export interface VaultSessionContextType {
  isVaultUnlocked: boolean;
  vaultToken: string | null;
  expiresAt: number | null;
  timeRemainingSeconds: number;
  formattedTimeRemaining: string;
  unlockVault: (totpCode: string) => Promise<{ success: boolean; usedRecoveryCode?: boolean }>;
  lockVault: () => Promise<void>;
  isUnlocking: boolean;
  error: string | null;
  setError: (err: string | null) => void;
}

const VaultSessionContext = createContext<VaultSessionContextType | null>(null);

export const VaultSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // STRICT SECURITY MANDATE: In-memory state ONLY. Zero localStorage / sessionStorage persistence.
  const [vaultToken, setVaultToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [timeRemainingSeconds, setTimeRemainingSeconds] = useState<number>(0);
  const [isUnlocking, setIsUnlocking] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const tokenRef = useRef<string | null>(null);
  tokenRef.current = vaultToken;

  // Register token getter with Axios client
  useEffect(() => {
    setVaultTokenGetter(() => tokenRef.current);
    return () => {
      setVaultTokenGetter(null);
    };
  }, []);

  const isVaultUnlocked = Boolean(vaultToken && expiresAt && Date.now() < expiresAt);

  const lockVault = useCallback(async () => {
    try {
      if (tokenRef.current) {
        await api.post('/auth/vault/lock').catch(() => {});
      }
    } finally {
      setVaultToken(null);
      setExpiresAt(null);
      setTimeRemainingSeconds(0);
      tokenRef.current = null;
    }
  }, []);

  const unlockVault = useCallback(async (totpCode: string) => {
    setIsUnlocking(true);
    setError(null);
    try {
      // Endpoint mounted under /api/v1/auth/vault and /api/v1/admin/auth/vault
      const response = await api.post('/auth/vault/verify', { totpCode });

      if (response.data?.status === 'success' && response.data?.vaultToken) {
        const token = response.data.vaultToken;
        const ttlSec = Number(response.data.expiresIn) || 900;
        const expiryTime = Date.now() + ttlSec * 1000;

        setVaultToken(token);
        setExpiresAt(expiryTime);
        setTimeRemainingSeconds(ttlSec);
        tokenRef.current = token;

        return {
          success: true,
          usedRecoveryCode: response.data.usedRecoveryCode || false
        };
      } else {
        throw new Error(response.data?.message || 'Verification failed');
      }
    } catch (err: any) {
      const serverMessage = err.response?.data?.message || err.message || 'Verification failed';
      const errorCode = err.response?.data?.code;
      const customErr: any = new Error(serverMessage);
      customErr.code = errorCode;
      customErr.remainingAttempts = err.response?.data?.remainingAttempts;
      customErr.lockoutUntil = err.response?.data?.lockoutUntil;
      setError(serverMessage);
      throw customErr;
    } finally {
      setIsUnlocking(false);
    }
  }, []);

  // Countdown & auto-lock interval ticker
  useEffect(() => {
    if (!isVaultUnlocked || !expiresAt) {
      setTimeRemainingSeconds(0);
      return;
    }

    const updateTimer = () => {
      const remainingMs = expiresAt - Date.now();
      if (remainingMs <= 0) {
        lockVault();
      } else {
        setTimeRemainingSeconds(Math.ceil(remainingMs / 1000));
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [isVaultUnlocked, expiresAt, lockVault]);

  const minutes = Math.floor(timeRemainingSeconds / 60);
  const seconds = timeRemainingSeconds % 60;
  const formattedTimeRemaining = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  const value: VaultSessionContextType = {
    isVaultUnlocked,
    vaultToken,
    expiresAt,
    timeRemainingSeconds,
    formattedTimeRemaining,
    unlockVault,
    lockVault,
    isUnlocking,
    error,
    setError
  };

  return (
    <VaultSessionContext.Provider value={value}>
      {children}
    </VaultSessionContext.Provider>
  );
};

export const useVaultSession = (): VaultSessionContextType => {
  const context = useContext(VaultSessionContext);
  if (!context) {
    throw new Error('useVaultSession must be used within a VaultSessionProvider');
  }
  return context;
};
